/**
 * Bulk Upload — Custom Fields Integration
 *
 * Converts project-defined CustomColumnDefinitions into UploadFieldDef entries
 * so they participate in column mapping, validation, template generation,
 * and row submission. Custom field values are grouped into a `customData`
 * JSON object on the API payload rather than top-level fields.
 */

import { CustomColumnDefinition, CustomFieldDefinition } from '../../../../@types';
import { FieldDataType, UploadFieldDef, UploadSchema } from './types';

// ---------------------------------------------------------------------------
// Type Mapping
// ---------------------------------------------------------------------------

/** Map CustomColumnType → UploadFieldDef.dataType */
function mapCustomType(type: CustomColumnDefinition['type']): FieldDataType {
	switch (type) {
		case 'text':
			return 'string';
		case 'number':
			return 'number';
		case 'date':
			return 'date';
		case 'boolean':
			return 'boolean';
		case 'select':
			return 'enum';
		default:
			return 'string';
	}
}

// ---------------------------------------------------------------------------
// Converter
// ---------------------------------------------------------------------------

/** Custom field name prefix to avoid collisions with built-in schema fields */
const CUSTOM_FIELD_PREFIX = 'cf_';

/**
 * Convert a CustomColumnDefinition into an UploadFieldDef.
 * The `name` is prefixed with `cf_` to avoid collisions with built-in fields.
 */
export function customColumnToFieldDef(col: CustomColumnDefinition): UploadFieldDef {
	const dataType = mapCustomType(col.type);

	const field: UploadFieldDef = {
		name: `${CUSTOM_FIELD_PREFIX}${col.id}`,
		label: col.name,
		dataType,
		required: col.isRequired,
		isCustomField: true,
		customFieldId: col.id,
		headerAliases: [col.name],
	};

	// Enum options for 'select' type
	if (dataType === 'enum' && col.options && col.options.length > 0) {
		field.enumValues = col.options;
	}

	// Validation constraints
	if (col.validation) {
		field.constraints = {};
		if (col.validation.min !== undefined) field.constraints.min = col.validation.min;
		if (col.validation.max !== undefined) field.constraints.max = col.validation.max;
		if (col.validation.minLength !== undefined)
			field.constraints.minLength = col.validation.minLength;
		if (col.validation.maxLength !== undefined)
			field.constraints.maxLength = col.validation.maxLength;
		if (col.validation.pattern) field.constraints.pattern = new RegExp(col.validation.pattern);
	}

	return field;
}

/**
 * Convert all active custom column definitions for a given entity type
 * into UploadFieldDef entries, sorted by their defined order.
 */
export function getCustomFieldDefs(
	definitions: CustomFieldDefinition[],
	entityType: string
): UploadFieldDef[] {
	const def = definitions.find((d) => d.entityType === entityType);
	if (!def) return [];

	return def.definitions
		.filter((col) => col.isActive)
		.sort((a, b) => a.order - b.order)
		.map(customColumnToFieldDef);
}

/**
 * Create a copy of an UploadSchema with custom fields appended.
 * Does not mutate the original schema.
 */
export function schemaWithCustomFields(
	schema: UploadSchema,
	definitions: CustomFieldDefinition[]
): UploadSchema {
	if (!schema.customFieldEntityType) return schema;

	const customFields = getCustomFieldDefs(definitions, schema.customFieldEntityType);
	if (customFields.length === 0) return schema;

	return {
		...schema,
		fields: [...schema.fields, ...customFields],
	};
}

/**
 * Extract custom field values from a resolved row and group them into a `customData` object.
 * Removes the `cf_` prefixed keys from the row and returns both the clean row and customData.
 */
export function extractCustomData(resolvedRow: Record<string, unknown>): {
	row: Record<string, unknown>;
	customData: Record<string, unknown>;
} {
	const row: Record<string, unknown> = {};
	const customData: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(resolvedRow)) {
		if (key.startsWith(CUSTOM_FIELD_PREFIX)) {
			// Strip the prefix to get the original column ID
			const colId = key.slice(CUSTOM_FIELD_PREFIX.length);
			customData[colId] = value;
		} else {
			row[key] = value;
		}
	}

	return { row, customData };
}
